import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { SigmClientDTO } from "./reporting";
import type {
  NamedDTO,
  ProductDescriptionDTO,
  ProductImageDTO,
  ProductKpiSalesDTO,
  ProductMetadataDTO,
  ProductPricingDTO,
  ProductStatus,
  ProductType,
} from "./product";

/** Per-product margin override; absent means the global rule applies. */
export interface ProductPricingRuleDTO {
  id: number;
  product_id?: number;
  gross_margin: number;
}

/** The pricing read model: product identity plus everything the maths needs. */
export interface LazyProductPricingDTO {
  id: number;
  type: ProductType;
  status: ProductStatus;
  designer?: (NamedDTO & { royalty?: number }) | null;
  category?: NamedDTO | null;
  metadata?: ProductMetadataDTO | null;
  description_en?: ProductDescriptionDTO | null;
  pricing?: ProductPricingDTO | null;
  kpi_sales?: ProductKpiSalesDTO | null;
  image?: ProductImageDTO | null;
  pricing_rule?: ProductPricingRuleDTO | null;
}

/** Per-client pricing adjustments, keyed by ERP client id. */
export interface ClientPricingRuleDTO {
  id: number;
  client_id: number;
  has_shipping: boolean;
  margin_percent_adjustment?: number | null;
  margin_amount_adjustment?: number | null;
}

/** `{first: currentPrice, second: profit, third: netMargin}` from the discount simulation. */
export interface DiscountProfit {
  first: number;
  second: number;
  third: number;
}

/**
 * Nexpim's own client resource. It serves the SIGM `cli_`-prefixed wire shape
 * (same DTO as the reporting endpoints), so the existing `SigmClientDTO`
 * applies. The pricing screens pick clients from here: `active` for the
 * pricing view, `lastorder` for discounts and the finance reports.
 */
export class NexpimClientClient extends ResourceClient<SigmClientDTO> {
  constructor(http: HttpClient) {
    super(http, "/client");
  }

  active(): Promise<SigmClientDTO[]> {
    return this.http.get<SigmClientDTO[]>("/client/active");
  }

  /** Clients that ordered within the last `years` years. */
  byLastOrder(years: number): Promise<SigmClientDTO[]> {
    return this.http.get<SigmClientDTO[]>(`/client/lastorder/${years}`);
  }
}

/** The `name - no - currency` label the Vaadin pickers used. */
export function clientLabel(client: SigmClientDTO): string {
  return (
    [client.cli_name1, client.cli_no, client.cli_currency].filter(Boolean).join(" - ") ||
    `#${client.cli_id}`
  );
}

export class LazyProductPricingClient extends ResourceClient<LazyProductPricingDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/lazy/pricing");
  }

  byTypeAndStatus(types: ProductType[], statuses: ProductStatus[]): Promise<LazyProductPricingDTO[]> {
    return this.http.post<LazyProductPricingDTO[]>("/product/lazy/pricing/search/type-and-status", {
      types,
      statuses,
    });
  }

  /** Bundle-aware COGS per product, computed server-side. */
  effectiveCogs(productIds: number[]): Promise<Record<string, number>> {
    return this.http.post<Record<string, number>>("/product/lazy/pricing/effective-cogs", productIds);
  }

  /** The server's suggested price per product for one client. */
  targetPrices(clientId: number, productIds: number[]): Promise<Record<string, number>> {
    return this.http.post<Record<string, number>>(
      `/product/lazy/pricing/target-price/client/${clientId}`,
      productIds,
    );
  }

  /** `{first: currentPrice, second: profit, third: netMargin}` tuples per product. */
  discountProfits(
    clientId: number,
    productIds: number[],
    discount: number,
  ): Promise<Record<string, DiscountProfit>> {
    return this.http.post(
      `/product/lazy/pricing/discount-profit/client/${clientId}?discount=${Math.round(discount)}`,
      productIds,
    );
  }

  /** Pricing calculator xlsx for the selected clients. */
  exportPricing(clientIds: number[]) {
    return this.http.job("/product/lazy/pricing/document/pricing", { body: clientIds });
  }

  /** Discounted-pricing xlsx for the selected clients at one discount level. */
  exportPricingDiscount(clientIds: number[], discount: number) {
    return this.http.job(`/product/lazy/pricing/document/pricing/discount?discount=${discount}`, {
      body: clientIds,
    });
  }
}

export class ClientPricingRuleClient extends ResourceClient<ClientPricingRuleDTO> {
  constructor(http: HttpClient) {
    super(http, "/client/pricing/rule");
  }

  /** All rules for the given clients in one round trip. */
  byClients(clientIds: number[]): Promise<ClientPricingRuleDTO[]> {
    return this.http.post<ClientPricingRuleDTO[]>("/client/pricing/rule/client", clientIds);
  }

  async byClient(clientId: number): Promise<ClientPricingRuleDTO | null> {
    try {
      return await this.http.get<ClientPricingRuleDTO>(`/client/pricing/rule/client/${clientId}`);
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  }
}
